import './index.scss';
import { X } from 'react-feather';
import Toast from '../Toast/Toast';
import { Input } from 'reactstrap';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastTypeEnum } from '../Toast/toast-type.enum';
import { useStartLoader, useStopLoader } from '../../../store/modal/modalHooks';
import { useRemoveServiceCallImage } from '../../../store/support-management/SupportHooks';

interface UploadImageInputProps {
  id?: string;
  className?: string;
  placeholder?: string;
  invalid?: boolean;
  previewHidden?: boolean;
  uploaderHook: (data: any, callback: any) => void; // The hook that you wrote to send the request to the service
  fileSize?: number; // in MB, default to 2MB
  acceptedFiles?: string; // default to only images...
  onChange: (value: string[] | '') => void;
  value: string[];
  hidden?: boolean;
  allowedFormats?: string[];
}

const DEFAULT_FILE_SIZE = 10; // MB
const DEFAULT_ACCEPTED_FILES_TYPES = 'image/*';

const UploadMultiImageInput = React.forwardRef(
  (props: UploadImageInputProps, ref) => {
    const { t } = useTranslation();
    const stopLoader = useStopLoader();
    const startLoader = useStartLoader();
    const removeServiceCallImage = useRemoveServiceCallImage();

    const [imageKey, setImageKey] = useState(1);

    const { uploaderHook, ...inputProps } = props;

    const getFormData = (file: any) => {
      const formData = new FormData();

      const [fileName, extension] = file.name.split('.');

      const hebrewRegex = /[\u0590-\u05FF]/;

      let newFileName = file.name;
      if (hebrewRegex.test(fileName)) {
        newFileName = `hebrewName${Date.now()}.${extension}`;
      }

      formData.append('file', file, newFileName);

      return formData;
    };

    const getAllowedSize = () => {
      return props.fileSize || DEFAULT_FILE_SIZE;
    };

    const imageSizeIsValid = (selectedFileSize: number) => {
      const allowedSize = getAllowedSize();
      return selectedFileSize <= allowedSize;
    };

    const handleChange = async (event: any) => {
      if (!event.target.files) {
        return;
      }

      const arrayOfFiles: any[] = Array.from(event.target.files);

      const allowedFormats = props.allowedFormats || [
        'image/jpeg',
        'image/png',
        'application/pdf',
        'image/gif',
      ];

      arrayOfFiles.forEach((file: any) => {
        if (!allowedFormats.includes(file.type)) {
          return Toast(
            ToastTypeEnum.ERROR,
            t('NO_ALLOWED_IMAGE_FORMAT_TEXT'),
            t
          );
        }
      });

      const uploadedImages: string[] = [];
      startLoader().finally();
      removeServiceCallImage();

      const addUploadedImage = async (file: any) => {
        const image = await uploadImage(file);
        uploadedImages.push(image);
      };

      const promises: any[] = [];

      arrayOfFiles.forEach((file) => {
        promises.push(addUploadedImage(file));
      });

      Promise.all(promises).finally(() => {
        stopLoader().finally();
        props.onChange(uploadedImages);
      });
    };

    const uploadImage = async (file: any): Promise<string> => {
      const selectedFileSize = file.size / 1024 / 1024; // MB

      return new Promise((resolve, reject) => {
        if (imageSizeIsValid(selectedFileSize)) {
          const data = getFormData(file);
          props.uploaderHook(data, (imageUrl: any, error: any) => {
            if (error) {
              reject(`Bad image format, try uploading another one`);
            }
            resolve(imageUrl);
          });
        } else {
          reject(
            `Image size too big, please upload an image less then ${getAllowedSize()} MB`
          );
        }
      });
    };

    const onRemoveImage = (index: number) => {
      if (props.value.length === 1 && props.onChange) {
        props.onChange('');
        removeServiceCallImage();
        return;
      }
      const newItems = props.value.filter((item, i) => {
        return index !== i;
      });
      removeServiceCallImage(index);
      props.onChange(newItems);
      setImageKey(imageKey + 1);
    };

    return (
      <div className={classNames({ hidden: props.hidden })}>
        <Input
          type="file"
          multiple
          accept={
            props.acceptedFiles || DEFAULT_ACCEPTED_FILES_TYPES || 'image/*'
          }
          key={imageKey}
          onChange={handleChange}
          invalid={props.invalid}
          capture="environment"
          max={'2Mb'}
        />
        {/*// @ts-ignore*/}
        <Input type="text" {...inputProps} className="hidden" />
        <div className="file-size-label">
          {t('MAX_FILE_SIZE', { size: '10MB' })}
        </div>
        {!props.previewHidden && props.value && (
          <div className="multi-images-previews">
            {props.value.map((image, index) => (
              <div className="file-preview" key={image}>
                <img src={image} alt="" />
                <div
                  className="file-preview-remove"
                  onClick={() => onRemoveImage(index)}
                >
                  <X />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
);

export default UploadMultiImageInput;
